import { postRostersByRosterIdEditMutation } from "@aura-frontend/omnibus-az-client";
import { getTeamsSearch } from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertBar,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Input,
  Spinner,
  toast,
} from "@win-reality/react-ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAppContext } from "@/hooks/use-app-context";
import { addOrgPrefix } from "@/lib/utils";

const changeJerseyNumberSchema = z.object({
  jerseyNumber: z.string().optional(),
});

type ChangeJerseyNumberFormSchema = z.infer<typeof changeJerseyNumberSchema>;

type ChangeJerseyNumberModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rosterId: string;
  playerId: string;
};

export default function ChangeJerseyNumberModal({
  open,
  onOpenChange,
  rosterId,
  playerId,
}: ChangeJerseyNumberModalProps) {
  const { orgId: rawOrgId } = useAppContext();
  const organizationId = rawOrgId ? addOrgPrefix(rawOrgId) : "";
  const queryClient = useQueryClient();

  const form = useForm<ChangeJerseyNumberFormSchema>({
    resolver: zodResolver(changeJerseyNumberSchema),
    defaultValues: {
      jerseyNumber: "",
    },
  });

  // Fetch current roster data to get unofficial_members and player info
  const { data: rosterData, isLoading } = useQuery({
    queryKey: ["getTeamsSearch", organizationId, rosterId],
    queryFn: async () => {
      if (!organizationId || !rosterId) return null;
      const response = await getTeamsSearch({
        query: {
          visibilityGroup: organizationId,
          externalId: rosterId,
        },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: open && !!organizationId && !!rosterId,
  });

  const roster = rosterData?.teams?.[0];
  const rosterRecord = roster as unknown as Record<string, unknown>;
  const rosterIdFromData =
    (rosterRecord?.id as string | undefined) ||
    (rosterRecord?.internal_id as string | undefined);
  const unofficialMembers = roster?.unofficial_members ?? [];

  // Find the current player and populate the form
  const currentPlayer = unofficialMembers.find((m) => {
    const memberRecord = m as unknown as Record<string, unknown>;
    return (
      (memberRecord.external_id as string | undefined) === playerId ||
      (memberRecord.externalId as string | undefined) === playerId
    );
  });

  const currentPlayerRecord = currentPlayer as unknown as Record<
    string,
    unknown
  >;
  const playerName = ((currentPlayerRecord?.player_name as
    | string
    | undefined) ||
    (currentPlayerRecord?.playerName as string | undefined) ||
    "") as string;

  // Update form when player data loads
  useEffect(() => {
    if (currentPlayer && open) {
      const jerseyNum =
        (currentPlayerRecord.jersey_number as string | number | undefined) ||
        (currentPlayerRecord.jerseyNumber as string | number | undefined);
      form.reset({
        jerseyNumber: jerseyNum ? String(jerseyNum) : "",
      });
    }
  }, [currentPlayer, open, form, currentPlayerRecord]);

  // Mutation to update roster (edit endpoint with unofficial_members)
  const updateRoster = useMutation({
    ...postRostersByRosterIdEditMutation(),
    onSuccess: () => {
      toast("Jersey number updated successfully", { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: ["getTeamsSearch"],
        exact: false,
      });
      onOpenChange(false);
    },
    onError: () => {
      toast("Failed to update jersey number", { variant: "error" });
    },
  });

  const handleSubmit = async (data: ChangeJerseyNumberFormSchema) => {
    // Build updated unofficialMembers with the new jersey number
    const updatedMembers = unofficialMembers.map((member) => {
      const memberRecord = member as unknown as Record<string, unknown>;
      const memberId =
        (memberRecord.external_id as string | undefined) ||
        (memberRecord.externalId as string | undefined);
      if (memberId === playerId) {
        return {
          ...member,
          jerseyNumber: data.jerseyNumber
            ? parseInt(data.jerseyNumber, 10)
            : null,
        };
      }
      return member;
    });

    // Call edit endpoint with updated unofficialMembers
    if (!rosterIdFromData) {
      toast("Error: Unable to find roster ID", { variant: "error" });
      return;
    }
    await updateRoster.mutateAsync({
      path: { roster_id: rosterIdFromData },
      body: {
        unofficialMembers: updatedMembers,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onOpenChange(false)}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Change Jersey Number</DialogTitle>
            </DialogHeader>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <>
                <AlertBar variant="warning">
                  To edit full player details, go to the{" "}
                  <strong>Players</strong> tab
                </AlertBar>

                <div className="grid gap-4 py-4">
                  <div>
                    <label
                      htmlFor="player-name-display"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Player Name:
                    </label>
                    <div id="player-name-display" className="mt-2 text-sm">
                      {playerName}
                    </div>
                  </div>
                </div>

                <Form {...form}>
                  <form
                    id="change-jersey-number-form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="grid gap-4"
                  >
                    <FormField
                      control={form.control}
                      name="jerseyNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Jersey Number:</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              placeholder="Jersey Number"
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </>
            )}
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={() => onOpenChange(false)}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                type="submit"
                form="change-jersey-number-form"
                isLoading={updateRoster.isPending || isLoading}
                isDisabled={updateRoster.isPending || isLoading}
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
